import { useState } from 'react'
import {
  Badge, Box, Button, HStack, Heading, IconButton, Link, SimpleGrid, Text,
} from '@chakra-ui/react'
import { FiCalendar, FiChevronDown, FiChevronUp, FiEdit2 } from 'react-icons/fi'
import { Card } from '../shared/Card'
import { LogisticsModal } from './LogisticsModal'
import { useLogistics } from '../../hooks/useLobbyDay'
import type { LogisticsDto } from '../../types/lobbyDay'

const labelSx = { fontSize: 'xs', color: 'muted', textTransform: 'uppercase', letterSpacing: 'wide' } as const

function hasText(v: string | null | undefined): v is string {
  return !!v && v.trim().length > 0
}

/** A section (meals/lodging/transport) counts as "set" once it has an owner or details. */
function sectionSet(owner: string | null, notes: string | null): boolean {
  return hasText(owner) || hasText(notes)
}

function anyLogistics(d: LogisticsDto): boolean {
  return [
    d.mealsOwner, d.mealsNotes, d.lodgingOwner, d.lodgingNotes,
    d.transportationOwner, d.transportationNotes, d.generalNotes,
    d.dayOfPocName, d.dayOfPocPhone,
  ].some(hasText)
}

function SummaryCell({ label, value, accent }: { label: string; value: string | null; accent?: boolean }) {
  return (
    <Box bg={accent ? 'accent.soft' : 'surface.2'} borderRadius="md" px={3} py={2}>
      <Text {...labelSx} mb={0.5}>{label}</Text>
      {hasText(value)
        ? <Text fontWeight="medium">{value}</Text>
        : <Text color="muted" fontStyle="italic">not set</Text>}
    </Box>
  )
}

/** LW-14: logistics summary banner atop the Schedule tab — glanceable owners + day-of contact, with
 *  an Edit pull-up. Replaces the standalone Logistics tab. Renders on both desktop and mobile hosts. */
export function LogisticsBanner({ eventId }: { eventId: string }) {
  const { data } = useLogistics(eventId)
  const [collapsed, setCollapsed] = useState(false)
  const [editing, setEditing] = useState(false)

  const dto = data
  const filled = dto
    ? [
        sectionSet(dto.mealsOwner, dto.mealsNotes),
        sectionSet(dto.lodgingOwner, dto.lodgingNotes),
        sectionSet(dto.transportationOwner, dto.transportationNotes),
      ].filter(Boolean).length
    : 0
  const isEmpty = !dto || !anyLogistics(dto)
  const pocLine = dto && hasText(dto.dayOfPocName)
    ? `${dto.dayOfPocName}${hasText(dto.dayOfPocPhone) ? ` · ${dto.dayOfPocPhone}` : ''}`
    : dto?.dayOfPocPhone ?? null

  return (
    <Card mb={4} px={4} py={3}>
      <HStack gap={2}>
        <HStack gap={2} flex="1" minW={0}>
          <Box color="accent" aria-hidden="true"><FiCalendar /></Box>
          <Heading size="sm">Logistics</Heading>
          {!isEmpty && <Badge colorPalette="brand" variant="subtle">{filled} of 3 set</Badge>}
        </HStack>
        <Button size="sm" variant="outline" aria-label="Edit logistics" onClick={() => setEditing(true)}>
          <FiEdit2 />Edit
        </Button>
        <IconButton
          size="sm" variant="ghost"
          aria-label={collapsed ? 'Expand logistics' : 'Collapse logistics'}
          onClick={() => setCollapsed((c) => !c)}
        >{collapsed ? <FiChevronDown /> : <FiChevronUp />}</IconButton>
      </HStack>

      {!collapsed && (
        isEmpty ? (
          <Text color="muted" fontSize="sm" mt={3}>
            No logistics set yet — <Link color="accent" onClick={() => setEditing(true)}>add meals, lodging,
            transport &amp; day-of contact</Link>.
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={2.5} mt={3}>
            <SummaryCell label="Day-of contact" value={pocLine} accent />
            <SummaryCell label="Meals" value={dto!.mealsOwner} />
            <SummaryCell label="Lodging" value={dto!.lodgingOwner} />
            <SummaryCell label="Transportation" value={dto!.transportationOwner} />
          </SimpleGrid>
        )
      )}

      <LogisticsModal eventId={eventId} isOpen={editing} onClose={() => setEditing(false)} />
    </Card>
  )
}
